/**
 * ClassSessions API Hooks - Sophisticated pattern with TanStack Query
 *
 * Provides type-safe API operations for ClassSessions with proper error handling,
 * caching, and validation following the established architecture pattern.
 */

import { useQuery, useQueryClient } from "@tanstack/react-query"

import { usePermissions } from "@/hooks/usePermissions"
import { api } from "@/services/api"
import type {
  ClassSessionCreate,
  ClassSessionCreateInput,
  ClassSessionFilters,
  ClassSessionListResponse,
  ClassSessionResponse,
  ClassSessionUpdate,
  ClassSessionUpdateInput,
} from "@/types/class_session"
import { unwrapStandardResponse } from "@/utils/apiResponse"

import { useApiMutation } from "./useApiMutation"

/**
 * Fetch paginated list of classSessions with permission checks
 */
export function useClassSessions(filters: ClassSessionFilters = {}) {
  const { hasPermission } = usePermissions()

  return useQuery({
    queryKey: ["classSessions", filters],
    queryFn: async (): Promise<ClassSessionListResponse> => {
      const response =
        await api.classSessions.readClassSessionsApiV1ClassSessionsGet({
          limit: filters.limit,
          skip: filters.skip,
          search: filters.search,
          sortBy: filters.sort_by,
          sortOrder: filters.sort_order,
          createdBy: filters.created_by,
          createdAfter: filters.created_after,
          createdBefore: filters.created_before,
          updatedAfter: filters.updated_after,
          updatedBefore: filters.updated_before,
        })

      return unwrapStandardResponse<ClassSessionListResponse>(
        response,
      ) as ClassSessionListResponse
    },
    enabled: hasPermission("class_sessions:read"),
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
  })
}

/**
 * Fetch single classSession by ID with permission checks
 */
export function useClassSession(class_sessionId: string) {
  const { hasPermission } = usePermissions()

  return useQuery({
    queryKey: ["classSessions", class_sessionId],
    queryFn: async (): Promise<ClassSessionResponse> => {
      const response =
        await api.classSessions.readClassSessionApiV1ClassSessionsClassSessionIdGet(
          {
            classSessionId: class_sessionId,
          },
        )

      return unwrapStandardResponse<ClassSessionResponse>(
        response,
      ) as ClassSessionResponse
    },
    enabled: !!class_sessionId && hasPermission("class_sessions:read"),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  })
}

/**
 * Create new classSession with validation and error handling
 */
export function useCreateClassSession() {
  return useApiMutation<ClassSessionResponse, unknown, ClassSessionCreateInput>(
    {
      mutationFn: async (class_sessionData) => {
        // Let the backend handle permission checks

        const class_sessionCreate: ClassSessionCreate = {
          class_template_id: class_sessionData.class_template_id,
          session_id: class_sessionData.session_id ?? null,
          instructor_id: class_sessionData.instructor_id ?? null,
          session_date: class_sessionData.session_date,
          session_start: class_sessionData.session_start,
          session_end: class_sessionData.session_end,
          status: class_sessionData.status ?? null,
          cancellation_reason: class_sessionData.cancellation_reason ?? null,
          location_override: class_sessionData.location_override ?? null,
          max_students_override:
            class_sessionData.max_students_override ?? null,
          notes: class_sessionData.notes ?? null,
        }

        const response =
          await api.classSessions.createClassSessionApiV1ClassSessionsPost({
            requestBody: class_sessionCreate,
          })

        return unwrapStandardResponse<ClassSessionResponse>(
          response,
        ) as ClassSessionResponse
      },
      invalidateQueries: [["classSessions"]],
      successToast: (data) => ({
        title: "ClassSession created successfully",
        description: ` "${data.id}" has been created.`,
      }),
    },
  )
}

/**
 * Update existing classSession with validation and error handling
 */
export function useUpdateClassSession() {
  return useApiMutation<
    ClassSessionResponse,
    unknown,
    { class_sessionId: string; class_sessionData: ClassSessionUpdateInput }
  >({
    mutationFn: async ({
      class_sessionId,
      class_sessionData,
    }: {
      class_sessionId: string
      class_sessionData: ClassSessionUpdateInput
    }) => {
      // Let the backend handle permission checks

      const class_sessionUpdate: ClassSessionUpdate = {
        class_template_id: class_sessionData.class_template_id,
        instructor_id: class_sessionData.instructor_id,
        session_date: class_sessionData.session_date,
        session_start: class_sessionData.session_start,
        session_end: class_sessionData.session_end,
        status: class_sessionData.status,
        cancellation_reason: class_sessionData.cancellation_reason,
        location_override: class_sessionData.location_override,
        max_students_override: class_sessionData.max_students_override,
        notes: class_sessionData.notes,
      }

      const response =
        await api.classSessions.updateClassSessionApiV1ClassSessionsClassSessionIdPut(
          {
            classSessionId: class_sessionId,
            requestBody: class_sessionUpdate,
          },
        )

      return unwrapStandardResponse<ClassSessionResponse>(
        response,
      ) as ClassSessionResponse
    },
    setQueryData: [
      {
        queryKey: (variables) => ["classSessions", variables.class_sessionId],
        updater: (_previous, data) => data,
      },
    ],
    invalidateQueries: [["classSessions"]],
  })
}

/**
 * Delete classSession with permission checks and error handling
 */
export function useDeleteClassSession() {
  return useApiMutation<{ class_sessionId: string }, unknown, string>({
    mutationFn: async (class_sessionId) => {
      // Let the backend handle permission checks
      await api.classSessions.deleteClassSessionApiV1ClassSessionsClassSessionIdDelete(
        { classSessionId: class_sessionId },
      )
      return { class_sessionId }
    },
    removeQueries: [(variables) => ["classSessions", variables]],
    invalidateQueries: [["classSessions"]],
  })
}

/**
 * Invalidate classSessions queries to force a refetch
 */
export function useInvalidateClassSessions() {
  const queryClient = useQueryClient()

  return () => {
    void queryClient.invalidateQueries({ queryKey: ["classSessions"] })
  }
}
